import React, { useState } from "react";
import { MenuItem, Separator, Text, Icon } from "@showitinc/showit-wc";
import { List as ImmutableList, Map } from "immutable";
import { defaultRangeExtractor, Virtualizer } from "@tanstack/react-virtual";

import AppConstants from "../../../../constants/AppConstants";
import AppConfig from "../../../../config/app";
import PanelHeader from "../../../../app2components/PanelHeader/PanelHeader";
import uiPrefix from "../../../../utils/UiPrefixUtils";
import SitePanelItem from "../../../../app2components/SitePanel/SitePanelItem/SitePanelItem";
import SitePanelPageContextMenu from "../../../../app2components/ContextMenus/SitePanelPageContextMenu";
import SitePanelBlockContextMenu from "../../../../app2components/ContextMenus/SitePanelBlockContextMenu";
import SitePanelCanvasSetContextMenu from "../../../../app2components/ContextMenus/SitePanelCanvasSetContextMenu";
import ContextMenuWrapper from "../../../../app2components/ContextMenus/ContextMenuWrapper";
import styles from "./PagesPanel.module.css";
import RangeManager, { parseRange } from "./RangeManager";
import VirtualList from "../../../base/VirtualList";
import {
  addPage,
  addPageFolder,
  addSiteBlock,
  addTemplate,
  addTemplateFolder,
  choosePage,
  chooseSiteBlock,
  chooseTemplate,
  createCanvasSet,
  isSelectedContainer,
  openSiteSettingsModal,
  openThemeModal,
  select,
  showAssetLibrary,
} from "./actions";
import useStoreState from "../../../providers/useStoreState";
import DesignViewStore from "../../../../stores/DesignViewStore";
import SitePanelFolder from "../../../../app2components/SitePanel/SitePanelFolder/SitePanelFolder";

/**
 * @typedef {import("@tanstack/react-virtual").VirtualItem} VirtualItem
 * @typedef {import("@tanstack/react-virtual").Virtualizer} Virtualizer
 * @typedef {import("react").ReactElement} ReactElement
 * @typedef {import("immutable").List} ImmutableList
 * @typedef {import("immutable").Map} ImmutableMap
 * @typedef {import("@tanstack/react-virtual").Range} TSRange
 */

const { SITE_ITEM_BLOCK, SITE_ITEM_CANVAS_SET, SITE_ITEM_PAGE, SITE_ITEM_SITE, SITE_ITEM_WP_PAGE } =
  AppConstants;

const HEADER = "header";
const SEPARATOR = "separator";

const HEADERS = {
  [SITE_ITEM_PAGE]: Map({
    id: `${SITE_ITEM_PAGE}-${HEADER}`,
    type: HEADER,
    icon: "sitePage",
    label: "Pages",
    menu: (
      <ContextMenuWrapper icon="add">
        <MenuItem onClick={choosePage}>Add from library</MenuItem>
        <MenuItem onClick={addPage}>Add Blank Page</MenuItem>
        <MenuItem onClick={addPageFolder}>Add Page Folder</MenuItem>
      </ContextMenuWrapper>
    ),
  }),
  [SITE_ITEM_WP_PAGE]: Map({
    id: `${SITE_ITEM_WP_PAGE}-${HEADER}`,
    type: HEADER,
    icon: "wordpress",
    label: "Blog Templates",
    menu: (
      <ContextMenuWrapper icon="add">
        <MenuItem onClick={chooseTemplate}>Add from library</MenuItem>
        <MenuItem onClick={addTemplate}>Add Blank Blog Template</MenuItem>
        <MenuItem onClick={addTemplateFolder}>Add Template Folder</MenuItem>
      </ContextMenuWrapper>
    ),
    tooltip:
      "This section contains templates for displaying blog content, which you edit directly in WordPress.",
  }),
  [SITE_ITEM_BLOCK]: Map({
    id: `${SITE_ITEM_BLOCK}-${HEADER}`,
    type: HEADER,
    icon: "siteCanvas",
    label: "Site Canvases",
    menu: (
      <ContextMenuWrapper icon="add">
        <MenuItem onClick={chooseSiteBlock}>Add from library</MenuItem>
        <MenuItem onClick={addSiteBlock}>Add Blank Site Canvas</MenuItem>
      </ContextMenuWrapper>
    ),
  }),
  [SITE_ITEM_CANVAS_SET]: Map({
    id: `${SITE_ITEM_CANVAS_SET}-${HEADER}`,
    type: HEADER,
    icon: "canvasSet",
    label: "Canvas Set",
    menu: (
      <ContextMenuWrapper icon="add">
        <MenuItem onClick={createCanvasSet}>Add Site Canvas Set</MenuItem>
      </ContextMenuWrapper>
    ),
  }),
};

const ITEM_CONTEXT_MENU = {
  [SITE_ITEM_PAGE]: ({ site, item, active }) => (
    <SitePanelPageContextMenu site={site} page={item} active={active} />
  ),
  [SITE_ITEM_BLOCK]: ({ item, active }) => (
    <SitePanelBlockContextMenu block={item} active={active} />
  ),
  [SITE_ITEM_CANVAS_SET]: ({ item, active }) => (
    <SitePanelCanvasSetContextMenu canvasSet={item} active={active} />
  ),
};

const _getSiteSettingsButton = () => {
  if (AppConfig.demoMode) {
    return null;
  }
  return (
    <div
      data-e2e={uiPrefix("site-settings-btn")}
      className={styles["settings-panel-button"]}
      onClick={openSiteSettingsModal}
    >
      <Icon size="16" type="settings" />
      <Text>Site Settings</Text>
    </div>
  );
};

/**
 * Renders a single sitePanelItem
 */
const ItemRenderer = ({
  blockContainer,
  dataIndex,
  index: passedIndex,
  item,
  listIndex,
  measure,
  site,
}) => {
  const id = item.get("id");
  const type = item.get("type");
  const itemType = item.get("itemType");
  const active = isSelectedContainer(type, id, blockContainer);
  const index = passedIndex ?? item.get("index");

  const props = {
    active,
    dataIndex,
    key: id,
    id,
    index,
    listIndex,
    measure,
    onSelect: (source) => select(source, blockContainer),
    source: item,
    type: itemType ?? type,
  };

  return (
    <SitePanelItem {...props}>{ITEM_CONTEXT_MENU[type]({ item, site, active })}</SitePanelItem>
  );
};

/**
 * Renders a folder item within the site panel.
 *
 * @param {Object} props
 * @param {number} props.dataIndex - Index from virtualizer. Used to determine index within the list when measuring.
 * @param {Object} props.blockContainer - The container for site blocks, used to determine active selections.
 * @param {ImmutableList<ImmutableMap>} props.items - Total list of panel items. For searching for children in this case.
 * @param {ImmutableMap} props.item - The current folder item being rendered.
 * @param {number} props.index - The index of the current item in the total item list.
 * @param {number} props.listIndex - Index relative to similar items. Effectively the total list filtered down to just what is relevant, and the index within that filtered down remainder.
 * @param {Virtualizer["measureElement"]} props.measure - Allows vlist to measure the element and shift/resize list accordingly
 * @param {Object} props.site - The current site information.
 * @returns {React.ReactElement} The `SitePanelFolder` component containing child items.
 */
const FolderRenderer = ({
  blockContainer,
  dataIndex,
  items,
  item,
  index,
  listIndex,
  measure,
  site,
}) => {
  const id = item.get("id");
  const type = item.get("type");

  const props = {
    dataIndex,
    key: id,
    id,
    index,
    listIndex,
    measure,
    source: item,
    type,
  };

  const children = items.filter((entry) => entry.get("pId") === id);

  return (
    <SitePanelFolder {...props}>
      {[
        ...children.map((childItem, i) => {
          const itemIndex = childItem.get("index");
          const childListIndex = childItem.get("listIndex");

          return (
            <ItemRenderer
              key={childItem.get("id")}
              item={childItem}
              index={itemIndex}
              listIndex={childListIndex}
              blockContainer={blockContainer}
              site={site}
            />
          );
        }),
      ]}
    </SitePanelFolder>
  );
};

/**
 * Renders a header item
 */
const HeaderRenderer = ({ data }) => (
  <PanelHeader
    label={
      <>
        <Icon type={data.get("icon")} size="20" />
        {data.get("label")}
      </>
    }
    panelType={SITE_ITEM_SITE}
    tooltip={data.get("tooltip")}
  >
    {data.get("menu")}
  </PanelHeader>
);

// Static property for identification - survives minification unlike function.name
HeaderRenderer.isHeader = true;

/**
 * Scopes props to the renderer on initial call, so each virtualRow can
 * @param {Object} props
 * @param {ImmutableMap} props.blockContainer
 * @param {ImmutableList} props.items
 * @param {ImmutableList} props.filteredItems
 * @param {ReturnType<typeof RangeManager>} props.filteredRangeManager
 * @param {ImmutableMap} props.site
 *
 * @returns {React.Element}
 */
const RowRenderer =
  ({ blockContainer, items, filteredItems, filteredRangeManager, site }) =>
  /**
   * @param {VirtualItem & { key?: string, measure: Virtualizer["measureElement"] }} VirtualList
   * @returns
   */
  ({ index: VListIndex, key, measure }) => {
    const item = filteredItems.get(VListIndex);
    const type = item.get("type");
    const localIndex = item.get("listIndex") ?? filteredRangeManager.findLocalIndex(VListIndex);
    const index = item.get("index", localIndex);
    const inFolder = !!item.get("pId", false);

    return type === HEADER ? (
      <HeaderRenderer data={item} key={key} />
    ) : type === "separator" ? (
      <Separator />
    ) : type === "folder" ? (
      <FolderRenderer
        dataIndex={VListIndex}
        blockContainer={blockContainer}
        index={index}
        item={item}
        items={items}
        key={key}
        listIndex={localIndex}
        measure={measure}
        site={site}
      />
    ) : !inFolder ? (
      <ItemRenderer
        dataIndex={VListIndex}
        blockContainer={blockContainer}
        item={item}
        index={index}
        key={key}
        listIndex={localIndex}
        measure={measure}
        site={site}
      />
    ) : null;
  };

const isBlog = (page) => page.get("wp", false) && !(page.get("home") === 1);

const SeparatorMap = Map({ type: "separator" });
const HeaderRangeMap = Map({
  type: HEADER,
  sticky: true,
});

const isSticky = (stickyBounds) => (index) => stickyBounds.includes(index);
const isActiveSticky = (activeStickyIndex) => (index) => activeStickyIndex === index;

/**
 *
 * @param {ReturnType<RangeManager>} filteredRangeManager
 * @returns {Array<number>}
 */
const getStickyRanges = (filteredRangeManager) => {
  const ranges = filteredRangeManager.findRanges((item) =>
    Map.isMap(item) ? item.get("sticky") : false,
  );

  // could be immutable if necessary
  return ranges
    .map(parseRange)
    .toJS()
    .flat()
    .sort((a, b) => a - b);
};

/**
 * Normalizes page, blog, site canvas, and canvas set data. Also generates a range manager instance.
 * @param {ImmutableList<ImmutableMap>} pages
 * @param {ImmutableList<ImmutableMap>} siteBlocks
 * @param {ImmutableList<ImmutableMap>} canvasSets
 * @returns {[ImmutableList, ImmutableList, ReturnType<typeof RangeManager>]}
 */
const prepareItems = (pages, siteBlocks, canvasSets) => {
  const typed = pages.map((entry, index) =>
    entry.update("type", undefined, (val) => val ?? SITE_ITEM_PAGE).set("index", index),
  );

  const staticPages = typed
    .filter((page) => !isBlog(page))
    .map((entry, listIndex) =>
      entry.merge({
        itemType: SITE_ITEM_PAGE,
        listIndex,
      }),
    );

  const blogPages = typed.filter(isBlog).map((entry, listIndex) =>
    entry.merge({
      itemType: SITE_ITEM_WP_PAGE,
      listIndex,
    }),
  );

  const typedCanvasSets = canvasSets.map((entry) => entry.set("type", SITE_ITEM_CANVAS_SET));

  const items = ImmutableList([HEADERS[SITE_ITEM_PAGE]])
    .concat(staticPages)
    .push(SeparatorMap.set("id", `${SEPARATOR}-0`))
    .push(HEADERS[SITE_ITEM_WP_PAGE])
    .concat(blogPages)
    .push(SeparatorMap.set("id", `${SEPARATOR}-1`))
    .push(HEADERS[SITE_ITEM_BLOCK])
    .concat(siteBlocks)
    .push(SeparatorMap.set("id", `${SEPARATOR}-2`))
    .push(HEADERS[SITE_ITEM_CANVAS_SET])
    .concat(typedCanvasSets);

  const filteredStaticPages = staticPages.filter((item) => !item.get("pId"));
  const filteredBlogPages = blogPages.filter((item) => !item.get("pId"));

  const filteredItems = ImmutableList([HEADERS[SITE_ITEM_PAGE]])
    .concat(filteredStaticPages)
    .push(SeparatorMap.set("id", `${SEPARATOR}-0`))
    .push(HEADERS[SITE_ITEM_WP_PAGE])
    .concat(filteredBlogPages)
    .push(SeparatorMap.set("id", `${SEPARATOR}-1`))
    .push(HEADERS[SITE_ITEM_BLOCK])
    .concat(siteBlocks)
    .push(SeparatorMap.set("id", `${SEPARATOR}-2`))
    .push(HEADERS[SITE_ITEM_CANVAS_SET])
    .concat(typedCanvasSets);

  // provides a quick way for vlist to match vlistIndex to sticky/items
  const filteredRangeManager = RangeManager()
    .append(HeaderRangeMap)
    .append(SITE_ITEM_PAGE, filteredStaticPages.size)
    .append(SEPARATOR)
    .append(HeaderRangeMap)
    .append(SITE_ITEM_WP_PAGE, filteredBlogPages.size)
    .append(SEPARATOR)
    .append(HeaderRangeMap)
    .append(SITE_ITEM_BLOCK, siteBlocks.size)
    .append(SEPARATOR)
    .append(HeaderRangeMap)
    .append(SITE_ITEM_CANVAS_SET, canvasSets.size)
    .append(SEPARATOR);

  return [items, filteredItems, filteredRangeManager];
};

/**
 * Preps a callback that takes in a TanstackVirtual range and a setter for the active sticky index
 * @param {Array<number>} stickyIndexes
 * @param {(number) => void} setActiveSticky
 * @returns
 */

const initStickyRangeExtractor =
  (stickyIndexes, setActiveSticky) =>
  /**
   * Takes in a TanstackVirtual range, and returns an array of numbers
   * @param {TSRange} range
   * @returns {number[]}
   */
  (range) => {
    const { startIndex } = range;
    const activeStickyIndex = stickyIndexes.toReversed().find((index) => startIndex >= index) ?? 0;

    setActiveSticky(activeStickyIndex);

    return [...new Set([activeStickyIndex ?? 0, ...defaultRangeExtractor(range)])].sort(
      (a, b) => a - b,
    );
  };

/**
 * Renders the page panel with design settings and a list of pages, templates, site canvases, and canvas sets.
 * @param {Object} props Destructured props object
 * @param {ImmutableList<ImmutableMap>} props.pages An immutable list of page maps that the site contains
 * @param {Object} props.site The current site information
 * @param {Object} props.blockContainer The container for site blocks, used to determine active selections
 * @param {ImmutableList<ImmutableMap>} props.siteBlocks An immutable list of site block objects available for the site
 * @param {ImmutableList<ImmutableMap>} props.canvasSets An immutable list of canvas set objects available for the site
 * @returns {React.ReactElement} The `SitePanel` component
 */

const PagesPanel = ({ pages, site, blockContainer, siteBlocks, canvasSets }) => {
  const [activeSticky, setActiveSticky] = useState();
  const [items, filteredItems, filteredRangeManager] = prepareItems(pages, siteBlocks, canvasSets);

  const stickyIndexes = getStickyRanges(filteredRangeManager);
  const rangeExtractor = initStickyRangeExtractor(stickyIndexes, setActiveSticky);
  const startIndex = items.findIndex((item) =>
    isSelectedContainer(item.get("type"), item.get("id"), blockContainer),
  );

  const scale = useStoreState(DesignViewStore, DesignViewStore.getUIScale);
  const size = (parseInt(scale) / 16) * 32;

  return (
    <div
      className={styles["panel-pages"]}
      data-intercom-target="Pages Panel"
      style={{ display: "flex", maxHeight: "100%", overflow: "hidden" }}
    >
      <div className={styles["settings-panel"]}>
        {_getSiteSettingsButton()}

        <div className={styles["settings-panel-button-wrapper"]}>
          <div
            data-e2e={uiPrefix("design-settings-btn")}
            className={styles["settings-panel-button"]}
            onClick={openThemeModal}
          >
            <Icon size="16" type="designSettings" />
            <Text>Design Settings</Text>
          </div>
        </div>

        <div
          data-e2e={uiPrefix("media-library-btn")}
          className={styles["settings-panel-button"]}
          onClick={showAssetLibrary}
        >
          <Icon size="16" type="mediaLibrary" />
          <Text>Media Library</Text>
        </div>
      </div>

      <Separator />

      <VirtualList
        // isActiveSticky={isActiveSticky(activeSticky)}
        // isSticky={isSticky(stickyIndexes)}
        // rangeExtractor={rangeExtractor}
        size={filteredItems.size}
        startIndex={startIndex}
        estimateSize={() => size}
        getItemKey={(index) => filteredItems.get(index)?.get("id")}
      >
        {RowRenderer({
          blockContainer,
          items,
          filteredItems,
          filteredRangeManager,
          site,
        })}
      </VirtualList>
    </div>
  );
};

export default PagesPanel;
